<script lang="ts">
	import { page } from '$app/state';
	import LoadingIndicator from '@astlai/common/LoadingIndicator.svelte';
	import Markdown from '#lib/Markdown.svelte';
	import MessageActions from '#lib/MessageActions.svelte';
	import type { CitationAttrs } from '#src/types/markdown.ts';
	import { findErrorMessage } from '@astlai/common/util/error-handling/find-error-message';
	import { getChatContext } from '#src/contexts/chat.context.svelte.ts';
	import { getDocumentContext } from '#src/contexts/documents.context.svelte.ts';
	import { ChatAutoScroll } from '#lib/util/chat-auto-scroll.svelte.ts';
	import CloseIcon from 'virtual:icons/material-symbols/close-rounded';
	import StopIcon from 'virtual:icons/material-symbols/stop-rounded';

	import IconCheck from 'virtual:icons/material-symbols/check-rounded';
	import RetryIcon from 'virtual:icons/material-symbols/repeat-rounded';
	import MessageAssets from '#lib/MessageAssets.svelte';
	import ImageLightbox from '#lib/ImageLightbox.svelte';
	import type { AssetRef } from '#src/types/chat.ts';
	import { getModalContext } from '@astlai/common/contexts/modal.context.svelte.ts';
	import ChatFooter from './ChatFooter.svelte';
	import { t } from './i18n/tranlsator.svelte';
	import ResearchInfoBlock from './ResearchInfoBlock.svelte';
	import LoadingSpinner from './LoadingSpinner.svelte';
	import { tooltip } from '@astlai/common/tooltip/tooltip.svelte.ts';
	import ToolTypeIcon from './ToolTypeIcon.svelte';

	const chatCtx = getChatContext();
	const docCtx = getDocumentContext();
	const modalCtx = getModalContext();

	const hideThinking = $derived(
		page.data.instance?.feature_flags?.queryClassification?.settings?.defaultClassification
			?.hide_thinking
	);

	const activeTool = $derived(
		chatCtx.latestAssistantMessage?.toolCalls?.find((tc) => tc.status === 'running')
	);
	const lastCompletedTool = $derived(
		activeTool
			? undefined
			: chatCtx.latestAssistantMessage?.toolCalls?.findLast(
					(tc) => tc.status === 'completed' || tc.status === 'error'
				)
	);
	const chatScroll = new ChatAutoScroll({
		getMessages: () => chatCtx.presentableMessages,
		isLatestInProgress: () => chatCtx.latestAssistantMessage?.status === 'in_progress',
		getExtraDep: () => chatCtx.conversation.id
	});

	function handleRefClick(attrs: CitationAttrs) {
		docCtx.selectByHash(attrs.hash);
	}

	function openUploadedImage(asset: AssetRef) {
		modalCtx.openModal(
			ImageLightbox,
			{ src: asset.url ?? '', alt: asset.name },
			{ appearance: 'fullscreen' }
		);
	}
</script>

<div class="chat-dialog">
	<div {@attach chatScroll.attach()} class="messages-container">
		<div class="messages-list">
			{#each chatCtx.presentableMessages as message, i (message.id)}
				{@const showResearch =
					message.role === 'assistant' &&
					(message.toolCalls?.length || (message.reasoning_content && !hideThinking))}
				<div class="message {message.role}">
					<!-- The gap separates the assets from the text, without leaving space when there's no text. -->
					<div class="message-body">
						<MessageAssets
							assets={message.assets}
							onimageclick={message.role === 'user' ? openUploadedImage : undefined}
						/>

						{#if showResearch || message.content}
							<div class="message-text">
								{#if showResearch}
									<ResearchInfoBlock
										toolCalls={message.toolCalls}
										reasoningContent={!hideThinking ? message.reasoning_content : undefined}
										isLive={chatCtx.isStreaming && message === chatCtx.latestAssistantMessage}
									/>
								{/if}
								{#if message.content}
									<div class="message-content">
										<Markdown source={message.content} onRefClick={handleRefClick}>
											{#if message.status === 'incomplete'}
												<div
													aria-label={t('chat.incompleteTooltip')}
													use:tooltip
													class="message-incomplete"
												>
													<StopIcon />
													<span>{t('chat.incomplete')}</span>
												</div>
											{/if}
										</Markdown>
										{#if message.role === 'user' && i === chatCtx.presentableMessages.length - 1 && !chatCtx.fetchable.fetching}
											<!-- only show retry if its the last message-->
											<button
												class="icon-button"
												use:tooltip
												aria-label={t('chat.retry')}
												onclick={() => chatCtx.retry()}><RetryIcon /></button
											>
										{/if}
									</div>
								{/if}
							</div>
						{/if}
					</div>

					{#if message.role === 'user' || ((message.status === 'completed' || message.status === 'incomplete') && i > 0)}
						<MessageActions {message} isLast={i === chatCtx.presentableMessages.length - 1} />
					{/if}
				</div>
			{/each}

			{#if chatCtx.fetchable.fetching && chatCtx.latestAssistantMessage?.status !== 'in_progress' && chatCtx.latestAssistantMessage?.status !== 'completed'}
				{@const msg = chatCtx.latestAssistantMessage}
				{@const displayTool = activeTool ?? lastCompletedTool}
				<div class="message loading">
					<LoadingIndicator />
					{#if msg?.status === 'reasoning'}
						<span>{t('chat.fetching.thinking')}</span>
					{:else if msg?.status === 'rag.tool_call' || msg?.status === 'rag.tool_result'}
						{#if displayTool}
							<div class="tool-status-icon">
								<ToolTypeIcon name={displayTool.name} />
							</div>
						{/if}
						<span>{displayTool?.description ?? t('chat.fetching.thinking')}</span>
						{#if activeTool}
							<LoadingSpinner />
						{:else if lastCompletedTool}
							<div class="tool-check-icon"><IconCheck /></div>
						{/if}
					{/if}
				</div>
			{/if}

			{#if chatCtx.fetchable.error}
				<div class="error-message">
					{findErrorMessage(chatCtx.fetchable.error)}
					<div class="actions">
						<button class="glass" onclick={() => chatCtx.retry()}>Retry</button>
						<button class="icon-button" onclick={() => (chatCtx.fetchable.error = null)}
							><CloseIcon /></button
						>
					</div>
				</div>
			{/if}

			<div class="spacer" aria-hidden="true" style:height={chatScroll.spacerHeight}></div>
		</div>
	</div>

	<ChatFooter />
</div>

<style lang="scss">
	@use '@astlai/common/styles/mixins' as lib;
	.chat-dialog {
		flex: 1 1 auto;
		overflow: hidden;
		@include lib.flex-column();
		background: var(--surface-2);

		.messages-container {
			flex: 1;
			overflow-y: auto;
			overflow-y: overlay;
			position: relative;

			@include lib.flex-column();
			@include lib.flex-align();

			.messages-list {
				position: relative;
				width: lib.$bp-sm;
				max-width: 100%;
				@include lib.flex-column();

				padding: 1rem;
				gap: 0.5rem;
				.message {
					font-size: 1rem;
					line-height: 1.5rem;
					@include lib.flex-column();
					// inherit, so a user message's bubble stays right-aligned and as wide as its text.
					.message-body,
					.message-text {
						@include lib.flex-column();
						align-items: inherit;
					}
					.message-body {
						gap: 0.5rem;
					}
					.message-content {
						padding: 0.5rem 0;
						@include lib.flex-row();
						@include lib.flex-align();
						gap: 0.5rem;
						button.icon-button {
							margin: -0.5rem;
						}
					}
					&.user {
						max-width: 80%;
						align-self: flex-end;
						@include lib.flex-align(flex-start, flex-end);
						// The message shrink-wraps its bubble, so an uploaded image (absolutely
						// positioned inside its frame) has no width to fill and would collapse to
						// its border. Give it a thumbnail's width instead.
						:global(.asset-frame) {
							flex: 0 0 12rem;
						}
						.message-content {
							border-radius: 1rem;
							padding: 0.5rem 1rem;
							background-color: var(--surface-3);
							color: var(--text-primary);
						}
					}
					&.assistant {
						align-self: flex-start;
						background-color: transparent;
						color: var(--text-primary);
						align-self: stretch;
					}
					&.loading {
						padding: 1rem 0;
						@include lib.flex-row();
						@include lib.flex-align();
						gap: 0.5rem;
						color: var(--text-secondary);
						@keyframes text-flow {
							0% {
								background-position: 100% 0;
							}

							to {
								background-position: 0% 0;
							}
						}
						.tool-status-icon {
							font-size: 0.875rem;
							@include lib.flex-row();
							@include lib.flex-align();
						}
						.tool-check-icon {
							font-size: 0.875rem;
							color: var(--text-tertiary);
							@include lib.flex-row();
							@include lib.flex-align();
						}
						span {
							color: transparent;
							background: linear-gradient(
								90deg,
								var(--text-tertiary),
								var(--text-secondary),
								var(--text-tertiary),
								var(--text-secondary),
								var(--text-tertiary)
							);
							background-repeat: no-repeat;
							-webkit-background-clip: text;
							background-clip: text;
							background-size: 200% 100%;
							animation: text-flow 1s linear infinite;
						}
					}
					.message-incomplete {
						@include lib.flex-row();
						@include lib.flex-align(flex-start, center);
						gap: 0.25rem;
						padding: 0 0.5rem 0 0.25rem;
						border-radius: 0.5rem;
						background-color: var(--surface-warning);
						color: var(--text-warning, var(--text-secondary));
						font-size: 0.75rem;
						justify-self: flex-start;
						align-self: flex-start;
					}

					&:hover {
						:global(.message-actions) {
							opacity: 1;
						}
					}
				}
			}
		}

		.error-message {
			color: var(--text-error);
			background-color: var(--surface-error);
			padding: 1rem;
			border-radius: 1rem;
			margin: 0.5rem 0;
			@include lib.flex-row();
			@include lib.flex-align(space-between, center);
			white-space: pre-line;
			.actions {
				@include lib.flex-row();
				gap: 0.5rem;
				button {
					flex: 0 0 auto;
				}
			}
		}
	}
</style>
